import { Flex, Input } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { MaskedInput } from '../../../components/MaskedInput'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { ApiErrorShape, toFormErrors, useAppToast } from '../../../helpers'
import { CoursesService } from '../../../services/CoursesService/CoursesService'
import { ISelectCourse } from '../../../services/CoursesService/interfaces'
import { FormLeadService } from '../../../services/FormLead/FormLeadService'
import { LeadSourceField } from './LeadSourceField'
import { ILeadConsultant, IManualLeadFormat, manualFormatOptions } from './models'

interface IProps {
  isOpen: boolean
  onClose: () => void
  onCreated: () => void
  canManageSources: boolean
}

interface IForm {
  student_name: string
  name: string
  email: string
  phone: string
  format: IManualLeadFormat | ''
  knowledge_area_id: number | ''
  course_id: number | ''
  source: string
  commercial_consultant_id: number | ''
}

const EMPTY_FORM: IForm = {
  student_name: '',
  name: '',
  email: '',
  phone: '',
  format: '',
  knowledge_area_id: '',
  course_id: '',
  source: '',
  commercial_consultant_id: ''
}

const EMPTY_ERRORS: Record<keyof IForm, string> = {
  student_name: '',
  name: '',
  email: '',
  phone: '',
  format: '',
  knowledge_area_id: '',
  course_id: '',
  source: '',
  commercial_consultant_id: ''
}

const BLACKLISTED_MESSAGE = 'Este e-mail está na blacklist.'

/** Registro manual de leads (ABRA-328), no lugar do formulário "novos-leads" do site antigo. */
export function FormLeadCreateModal({ isOpen, onClose, onCreated, canManageSources }: IProps) {
  const service = new FormLeadService()
  const coursesService = new CoursesService()
  const toast = useAppToast()
  const [saving, setSaving] = useState(false)
  const [isLoadingOptions, setIsLoadingOptions] = useState(true)
  const [courses, setCourses] = useState<ISelectCourse[]>([])
  const [consultants, setConsultants] = useState<ILeadConsultant[]>([])
  const [blacklist, setBlacklist] = useState<Set<string>>(new Set())
  const [form, setForm] = useState<IForm>(EMPTY_FORM)
  const [formErrors, setFormErrors] = useState(EMPTY_ERRORS)

  const loadOptions = async () => {
    setIsLoadingOptions(true)

    try {
      const [courseList, consultantList] = await Promise.all([coursesService.listForSelect(), service.getConsultants()])
      setCourses(courseList)
      setConsultants(consultantList)
    } catch (error) {
      toast.error('Erro ao carregar as opções do formulário', error)
    } finally {
      setIsLoadingOptions(false)
    }
  }

  // O aviso é só uma antecipação: quem barra o e-mail é a API, então uma falha aqui não bloqueia nada.
  const loadBlacklist = async () => {
    try {
      const emails = await service.getBlacklistedEmails()
      setBlacklist(new Set(emails.map((email) => email.trim().toLowerCase())))
    } catch {
      setBlacklist(new Set())
    }
  }

  useEffect(() => {
    if (!isOpen) return

    setForm(EMPTY_FORM)
    setFormErrors(EMPTY_ERRORS)
    loadOptions()
    loadBlacklist()
  }, [isOpen])

  const coursesOfFormat = form.format ? courses.filter((course) => course[form.format as IManualLeadFormat]) : []

  const areas = Array.from(
    new Map(
      coursesOfFormat.flatMap((course) => course.knowledge_areas ?? []).map((area) => [area.id, area.name])
    ).entries()
  )
    .map(([id, name]) => ({ value: id, label: name }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))

  const coursesOfArea = coursesOfFormat.filter((course) =>
    course.knowledge_areas?.some((area) => area.id === form.knowledge_area_id)
  )

  const isBlacklisted = blacklist.has(form.email.trim().toLowerCase())

  // Como no formulário antigo: o nome do lead acompanha o do aluno até ser editado à parte.
  const handleStudentNameChange = (studentName: string) => {
    setForm((prev) => ({
      ...prev,
      student_name: studentName,
      name: prev.name === '' || prev.name === prev.student_name ? studentName : prev.name
    }))
  }

  const handleSave = async () => {
    if (!form.course_id) {
      setFormErrors({ ...EMPTY_ERRORS, course_id: 'O curso é obrigatório.' })
      toast.invalidForm()
      return
    }

    setSaving(true)
    setFormErrors(EMPTY_ERRORS)

    try {
      await service.create({
        ...form,
        format: form.format as IManualLeadFormat,
        knowledge_area_id: Number(form.knowledge_area_id),
        course_id: Number(form.course_id),
        commercial_consultant_id: Number(form.commercial_consultant_id)
      })
      toast.success('Lead registrado')
      onCreated()
      onClose()
    } catch (error) {
      const { status, data } = (error as ApiErrorShape).response ?? {}

      // A API responde 400 aos erros de validação (Exceptions\Handler); o 422 fica de reserva.
      if (status === 400 || status === 422) setFormErrors(toFormErrors(EMPTY_ERRORS, data?.errors))
      else toast.error('Erro ao registrar lead', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Registro manual de Leads"
      onSubmit={handleSave}
      isLoading={saving}
      isConfirmDisabled={isBlacklisted}
    >
      <Flex direction="column" gap={4}>
        <FormField label="Nome do Aluno" error={formErrors.student_name} isRequired>
          <Input
            value={form.student_name}
            onChange={(e) => handleStudentNameChange(e.target.value)}
            isDisabled={saving}
          />
        </FormField>

        <FormField label="Nome do Lead" error={formErrors.name} isRequired>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} isDisabled={saving} />
        </FormField>

        <FormField
          label="E-mail do Lead"
          error={formErrors.email || (isBlacklisted ? BLACKLISTED_MESSAGE : '')}
          isRequired
        >
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            isDisabled={saving}
          />
        </FormField>

        <FormField label="Telefone do Lead" error={formErrors.phone} isRequired>
          <MaskedInput
            mask="phone"
            value={form.phone}
            onChange={(phone) => setForm({ ...form, phone })}
            isDisabled={saving}
          />
        </FormField>

        <FormField label="Formato" error={formErrors.format} isRequired>
          <FormSelect
            value={form.format}
            onChange={(format) =>
              setForm({ ...form, format: format as IManualLeadFormat | '', knowledge_area_id: '', course_id: '' })
            }
            options={manualFormatOptions}
            isDisabled={saving}
          />
        </FormField>

        <FormField label="Área de Conhecimento" error={formErrors.knowledge_area_id} isRequired>
          <FormSelect
            value={form.knowledge_area_id}
            onChange={(areaId) => setForm({ ...form, knowledge_area_id: areaId ? Number(areaId) : '', course_id: '' })}
            options={areas}
            isLoading={isLoadingOptions}
            isDisabled={saving || !form.format}
            placeholder={form.format ? 'Selecione' : 'Selecione o formato primeiro'}
          />
        </FormField>

        <FormField label="Curso" error={formErrors.course_id} isRequired>
          <SearchableSelect<number>
            value={form.course_id}
            onChange={(courseId) => setForm({ ...form, course_id: courseId })}
            options={coursesOfArea.map((course) => ({ value: course.id, label: course.name }))}
            isLoading={isLoadingOptions}
            isDisabled={saving || !form.knowledge_area_id}
            placeholder={form.knowledge_area_id ? 'Selecione' : 'Selecione a área primeiro'}
            entity="curso"
          />
        </FormField>

        <LeadSourceField
          value={form.source}
          onChange={(source) => setForm((prev) => ({ ...prev, source }))}
          error={formErrors.source}
          isRequired
          isDisabled={saving}
          canManage={canManageSources}
        />

        <FormField label="Consultor Comercial" error={formErrors.commercial_consultant_id} isRequired>
          <FormSelect
            value={form.commercial_consultant_id}
            onChange={(consultantId) =>
              setForm({ ...form, commercial_consultant_id: consultantId ? Number(consultantId) : '' })
            }
            options={consultants.map((consultant) => ({ value: consultant.id, label: consultant.name }))}
            isLoading={isLoadingOptions}
            isDisabled={saving}
          />
        </FormField>
      </Flex>
    </FormModal>
  )
}
